import { SupportChatApiError } from './types';
import { supportChatClientContextHeaders, supportChatFetch } from './http';
async function parseJson(res) {
    const text = await res.text();
    if (!text)
        return undefined;
    try {
        return JSON.parse(text);
    }
    catch {
        throw new SupportChatApiError('Invalid JSON response', res.status, text);
    }
}
async function expectOk(res) {
    if (res.ok)
        return;
    let body;
    try {
        body = await res.json();
    }
    catch {
        body = await res.text();
    }
    throw new SupportChatApiError(`Support chat HTTP ${res.status}`, res.status, body);
}
function asConversationArray(body) {
    if (Array.isArray(body))
        return body;
    if (body && typeof body === 'object') {
        const o = body;
        if (Array.isArray(o.conversations))
            return o.conversations;
        if (Array.isArray(o.data))
            return o.data;
    }
    return [];
}
function asMessagesPage(body) {
    if (body && typeof body === 'object' && 'messages' in body) {
        return body;
    }
    throw new SupportChatApiError('Unexpected messages response shape', 500, body);
}
/** POST /messages: `{ messages: [customer, ai?], ai_status? }`. */
function asSendMessageResult(body) {
    if (body && typeof body === 'object' && 'messages' in body) {
        const o = body;
        if (Array.isArray(o.messages)) {
            return {
                messages: o.messages,
                ai_status: typeof o.ai_status === 'string'
                    ? o.ai_status
                    : undefined
            };
        }
    }
    throw new SupportChatApiError('Unexpected send message response shape', 500, body);
}
export async function createSupportConversation(opts = {}) {
    const browserLocale = opts.browserLocale?.trim();
    const headers = {};
    if (browserLocale)
        headers['x-locale'] = browserLocale;
    const init = { method: 'POST', headers };
    const res = await supportChatFetch('/conversations', init);
    await expectOk(res);
    return parseJson(res);
}
export async function listSupportConversations() {
    const res = await supportChatFetch('/conversations', { method: 'GET' });
    await expectOk(res);
    const body = await parseJson(res);
    return asConversationArray(body);
}
export async function getSupportConversation(id) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(id)}`, { method: 'GET' });
    await expectOk(res);
    return parseJson(res);
}
/** Mirrors chat-service `MESSAGE__PLAYER_MAX_BYTES` (default, v-0.0.37+). */
export const SUPPORT_CHAT_PLAYER_MESSAGE_MAX_BYTES = 8 * 1024;
/** Mirrors chat-service `MESSAGE__GUEST_MAX_BYTES` (default, v-0.0.37+). */
export const SUPPORT_CHAT_GUEST_MESSAGE_MAX_BYTES = 4 * 1024;
/** Byte length as the backend measures it: UTF-8 bytes, not JS string length/codepoints. */
export function supportChatMessageByteLength(content) {
    return new TextEncoder().encode(content).length;
}
/**
 * Single source of truth for the actor-kind -> message byte cap mapping. `undefined` (actor not
 * yet resolved) falls back to the wider player cap, same as an unauthenticated/undetermined guest
 * would otherwise see a stricter limit than actually applies once resolved.
 */
export function supportChatMessageMaxBytesForActorKind(actorKind) {
    return actorKind === 'guest'
        ? SUPPORT_CHAT_GUEST_MESSAGE_MAX_BYTES
        : SUPPORT_CHAT_PLAYER_MESSAGE_MAX_BYTES;
}
/**
 * Returns messages from the send response: index 0 is the customer message; index 1 when present
 * is a synchronous AI reply. With human handling there is no second element; further messages may
 * still arrive via WebSocket.
 */
export async function sendSupportMessage(conversationId, opts) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
        method: 'POST',
        headers: supportChatClientContextHeaders(opts.browserLocale),
        json: {
            content: opts.content ?? '',
            attachments: opts.attachments ?? []
        }
    });
    await expectOk(res);
    const body = await parseJson(res);
    const result = asSendMessageResult(body);
    if (!result.messages.length) {
        throw new SupportChatApiError('Send message returned no messages', res.status, body);
    }
    return result;
}
export async function getSupportConversationMessages(conversationId, limit = 50, offset = 0) {
    const q = new URLSearchParams({
        limit: String(limit),
        offset: String(offset)
    });
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/messages?${q.toString()}`, { method: 'GET' });
    await expectOk(res);
    const body = await parseJson(res);
    return asMessagesPage(body);
}
export async function requestSupportHandoff(conversationId, reason) {
    const res = await supportChatFetch(`/conversations/${encodeURIComponent(conversationId)}/handoff`, {
        method: 'POST',
        json: { reason }
    });
    await expectOk(res);
}
